import React, {Fragment, useEffect, useState} from 'react';

import {Link} from 'react-router';
import styled from 'styled-components';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {useCurrentUserState} from 'app/current-user/api';
import {CollapsableFolderPickerForm} from 'app/folders/components/CollapsableFolderPickerForm';
import {DocumentUploaderArea} from 'app/general/widgets/DocumentUploaderArea';
import StateButton from 'app/general/widgets/StateButton';
import {CollapsableTeamPickerForm} from 'app/teams/components/CollapsableTeamPickerForm';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import {useImportDocument} from '../api';

import type {ImportableDocsContentType} from 'app/docimport/types';
import type {FolderSerializedType, TeamSerializedType} from 'sweet-types';

const StyledDropZoneDiv = styled.div`
  .upload-area-container {
    position: relative;

    .select-a-team-message {
      visibility: visible;
      position: absolute;
      background: rgba(255, 255, 255, 0.95);
      z-index: 2;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      transition: all 0.5s;
      opacity: 1;

      &.hidden {
        visibility: hidden;
        opacity: 0;
      }
    }
  }

  &.idle .file-upload {
    border-width: 4px;
    height: 190px;
    padding: 30px 0;
    opacity: 1;
  }

  &.idle .teams-list,
  &.uploaded .teams-list,
  &.uploading .teams-list {
    height: auto;
    opacity: 1;
  }

  &.uploaded .create-document,
  &.uploading .create-document {
    opacity: 1;
  }

  &.uploaded .uploading-content,
  &.uploading .uploading-content {
    height: auto;
    opacity: 1;
  }

  &.uploaded .uploads-list,
  &.uploading .uploads-list {
    height: auto;
    opacity: 1;
  }

  .create-document {
    opacity: 0;
  }

  .uploads-list,
  .file-upload,
  .teams-list,
  .uploading-content,
  .processing-content,
  .error-content,
  .success-content {
    position: relative;
    height: 0;
    border-width: 0;
    overflow: hidden;
    transition: all 0.25s;
    padding: 0;
    opacity: 0;
  }

  &.processing .processing-content,
  &.success .success-content,
  &.error .error-content {
    height: 100px;
    opacity: 1;
  }
`;
type Props = {
  show: boolean;
  targetContentType: ImportableDocsContentType;
  onClose: () => void;
};

type ObjectToImport = {
  key: string;
  originalFilename: string;
};

export const UploadDocumentsModal = ({
  show,
  targetContentType,
  onClose,
}: Props) => {
  const [status, setStatus] = useState<
    'idle' | 'uploading' | 'uploaded' | 'processing' | 'success' | 'error'
  >('idle');
  const [objectToImport, setObjectToImport] = useState<
    ObjectToImport | undefined
  >(undefined);
  const [teams, setTeams] = useState<Array<TeamSerializedType>>([]);
  const [folders, setFolders] = useState<Array<FolderSerializedType>>([]);
  const [docURL, setDocURL] = useState<string | undefined>(undefined);
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);
  // once we close the modal, we blow away the old component and never will get it back.
  const [isClosed, setIsClosed] = useState<boolean>(false);

  const [importDocument] = useImportDocument();

  const {is_super_manager, is_super_teammate} = useCurrentUserState().data!;
  const isSuper = is_super_manager || is_super_teammate || false;

  // prevents state updates after component unmount.
  useEffect(() => () => setIsClosed(true), []);

  const updateObjectToImport = (doc) => {
    setStatus('uploaded');
    setObjectToImport(doc);
  };

  const startImportTask = () => {
    if (objectToImport == null) {
      return;
    }

    setStatus('processing');
    const {key, originalFilename} = objectToImport;
    importDocument({
      key,
      original_filename: originalFilename,
      target_content_type: targetContentType,
      teams,
      folders,
    })
      .unwrap()
      .then(
        (result) => {
          if (!isClosed) {
            setStatus('success');
            setDocURL(result.result);
          }
        },
        (errorCode) => {
          if (!isClosed) {
            setStatus('error');
            setErrorCode(errorCode);
          }
        }
      );
  };

  const resetState = () => {
    setTeams([]);
    setFolders([]);
    setStatus('idle');
    setDocURL(undefined);
    setErrorCode(undefined);
  };

  const teamsOk = isSuper || teams.length > 0;
  const ct = getContentType(targetContentType);
  return (
    <TabbedModal
      onClosed={resetState}
      show={show}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'file-upload',
          name: gt('Upload document'),
          icon: 'fa fa-upload',
          content: (
            <Fragment>
              <StyledDropZoneDiv className={status}>
                <div className="upload-area-container">
                  <div
                    className={`select-a-team-message d-flex align-items-center justify-content-center ${
                      teamsOk ? 'hidden' : ''
                    }`}
                  >
                    <div className="lead text-info">
                      <i className="fa fa-fw fa-info-circle" />{' '}
                      {gt('Please select a team to continue')}
                    </div>
                  </div>
                  <DocumentUploaderArea
                    onStart={() => setStatus('uploading')}
                    onCancel={resetState}
                    onComplete={updateObjectToImport}
                  />
                </div>
                <div className="teams-list mt-2">
                  <CollapsableTeamPickerForm
                    selected={teams}
                    onChange={setTeams}
                  />
                  <CollapsableFolderPickerForm
                    onChange={(folders: Array<FolderSerializedType>) =>
                      Promise.resolve(setFolders(folders))
                    }
                  />
                </div>

                <div className="processing-content d-flex align-content-center">
                  <div className="text-center flex-grow-1">
                    <div>
                      {gt(
                        'This can take some minutes, you can close this modal.'
                      )}
                    </div>
                    <p>
                      {gt(
                        'We will send you an email when your %(docType)s is ready.',
                        {
                          docType: ct.name,
                        }
                      )}
                    </p>
                    <div className="progress">
                      <div className="px-1 progress-bar progress-bar-striped progress-bar-animated w-100">
                        {gt('Processing file...')}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="success-content d-flex align-content-center">
                  <div className="lead text-center flex-grow-1">
                    <p>
                      <i className="fa fa-fw fa-check-circle text-success" />{' '}
                      {gt('Your document has been imported.')}
                    </p>
                    {docURL != null ? (
                      <Link to={docURL} className="btn btn-primary">
                        {gt('View %(docType)s', {
                          docType: ct.name,
                        })}
                      </Link>
                    ) : null}
                  </div>
                </div>
                <div className="error-content d-flex align-content-center">
                  <div className="text-center flex-grow-1">
                    <p className="text-danger lead">
                      <i className="fa fa-fw fa-exclamation-triangle" />{' '}
                      {gt("Couldn't import your document")}
                    </p>
                    <div>
                      {errorCode === 'invalid_document'
                        ? gt(
                            'Make sure the file you uploaded is a valid Word document file.'
                          )
                        : gt('Something went wrong. Please try again later.')}
                    </div>
                  </div>
                </div>
                <p>
                  {gt(
                    'Need Help? View our %(docGuide)s for tips and template information.',
                    {
                      docGuide: (
                        <a
                          href="https://www.sweetprocess.com/kb/8LBTequD/article/WpHZf6AE9/document-import-guide/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {gt('Document Import Guide')}
                        </a>
                      ),
                    }
                  )}
                </p>
                <StyledModalFooter className="mt-3">
                  <button onClick={onClose} className="btn btn-link btn-sm">
                    <i className="fa fa-times" /> {gt('Close')}
                  </button>
                  {status === 'uploaded' || status === 'uploading' ? (
                    <StateButton
                      onClick={startImportTask}
                      disabled={status !== 'uploaded'}
                      busyText={gt('Document uploading')}
                      isIdle={status !== 'uploading'}
                      className="btn btn-primary create-document"
                    >
                      {gt('Create document')}
                    </StateButton>
                  ) : null}
                </StyledModalFooter>
              </StyledDropZoneDiv>
            </Fragment>
          ),
        },
      ]}
    />
  );
};
